import React, { useEffect, useRef, useState } from 'react';

/**
 * The player's picture panel. Shows the newest still AT OR BEFORE the current scene from the
 * primary video, else the poster, else nothing (no empty box: samples and non-director tiers have
 * neither). Stills cover ~10 scenes in 60, so a picture is usually HELD over from an earlier scene;
 * it is then dimmed and labelled "From scene N" so it is never mistaken for the scene being heard.
 *
 * images = { poster: url|null, stills: [{scene, title, url}] } from GET {base}/scenes.
 * Frames are square (1024×1024): the box is square and nothing is cropped (landmine 141).
 */

/** Pure lookup, so a jump to any scene lands on the right picture without stepping through scenes. */
export function pickPicture(images, sceneNo, bad) {
  if (!images) return null;
  const stills = (images.stills ?? []).filter((s) => !bad.has(s.url));
  let hit = null;
  if (sceneNo != null) for (const s of stills) { if (s.scene <= sceneNo) hit = s; else break; }   // server sorts by scene
  if (hit) return { kind: 'still', scene: hit.scene, title: hit.title, url: hit.url, held: hit.scene !== sceneNo };
  if (images.poster && !bad.has(images.poster)) return { kind: 'poster', url: images.poster, held: false };
  return null;
}

/** The next picture after this scene, to load before it is needed. */
export function nextPictureUrl(images, sceneNo, bad) {
  if (!images || sceneNo == null) return null;
  const s = (images.stills ?? []).find((x) => x.scene > sceneNo && !bad.has(x.url));
  return s ? s.url : null;
}

export function pictureLabel(p) {
  if (!p) return '';
  if (p.kind === 'poster') return 'Poster';
  const t = p.title ? ` · ${p.title}` : '';
  return p.held ? `From scene ${p.scene}${t}` : `Scene ${p.scene}${t}`;
}

/**
 * Crossfades between pictures. Images are rendered as a list keyed by URL, so the incoming one
 * keeps its element (and its slow zoom) when it becomes the shown one — no jump at the end of a fade.
 */
export function Picture({ pick, onBad, onOpen, compact }) {
  const [shown, setShown] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);
  const url = pick?.url ?? null;

  useEffect(() => {
    clearTimeout(timer.current);
    if (!url) { setShown(null); setIncoming(null); return; }
    if (url === shown) { setIncoming(null); return; }
    setIncoming(url); setLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onLoad = (u) => {
    if (u !== incoming) return;
    // The next picture is preloaded, so it usually loads from cache before the browser has painted
    // it at opacity 0 — flipping the class then would skip the fade. Wait two frames first.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setLoaded(true);
      timer.current = setTimeout(() => { setShown(u); setIncoming(null); }, 450);
    }));
  };
  const fail = (u) => {
    if (u === incoming) setIncoming(null);
    if (u === shown) setShown(null);
    onBad(u);
  };

  if (!pick) return null;
  const layers = [shown, incoming].filter((u, i, a) => u && a.indexOf(u) === i);
  const label = pictureLabel(pick);
  return (
    <button type="button" className={`tr-pic ${compact ? 'compact' : ''} ${pick.held ? 'held' : ''}`}
            onClick={onOpen} title={`${label} — click to enlarge`} aria-label={`${label}. Enlarge picture`}>
      <span className="tr-pic-box">
        {layers.map((u) => (
          <img key={u} src={u} alt="" draggable={false}
               className={u === incoming ? (loaded ? 'in' : 'next') : ''}
               onLoad={() => onLoad(u)} onError={() => fail(u)} />
        ))}
        {compact && <span className="tr-pic-badge">{pick.kind === 'poster' ? 'Poster' : `Sc ${pick.scene}`}</span>}
      </span>
      {!compact && <span className="tr-pic-cap">{label}</span>}
    </button>
  );
}

/** Full-size view. When the picture is held over from an earlier scene, offer to go there. */
export function PictureViewer({ pick, canGo, onGo, onClose }) {
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    addEventListener('keydown', k, true); return () => removeEventListener('keydown', k, true);
  }, [onClose]);
  if (!pick) return null;
  return (
    <div className="tr-fb-backdrop tr-lb" onClick={onClose}>
      <figure className="tr-lb-fig" role="dialog" aria-modal="true" aria-label={pictureLabel(pick)} onClick={(e) => e.stopPropagation()}>
        <img src={pick.url} alt="" />
        <figcaption>
          <span>{pictureLabel(pick)}</span>
          <span className="tr-lb-actions">
            {canGo && <button className="tr-btn" onClick={onGo}>Go to scene {pick.scene}</button>}
            <button className="tr-btn tr-play" onClick={onClose} autoFocus>Close</button>
          </span>
        </figcaption>
      </figure>
    </div>
  );
}

export const PICTURE_CSS = `
.tr-body{display:flex;justify-content:center;align-items:flex-start;gap:28px;padding:0 16px}
.tr-body .tr-page{flex:0 1 760px;min-width:0;margin:0;padding-left:0;padding-right:0}
.tr-side{flex:0 0 280px;position:sticky;top:18px;margin-top:18px}
.tr-pic{display:block;width:100%;padding:0;border:0;background:none;cursor:zoom-in;text-align:left;font:inherit;color:inherit}
.tr-pic:focus-visible{outline:2px solid #14536e;outline-offset:3px;border-radius:10px}
.tr-pic-box{position:relative;display:block;width:100%;aspect-ratio:1/1;overflow:hidden;border-radius:10px;
  background:#eef4f7;box-shadow:0 8px 24px -14px rgba(16,45,56,.45)}
.tr-pic-box img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;
  transition:opacity .4s ease, filter .4s ease;animation:tr-kb 26s ease-in-out infinite alternate}
.tr-pic-box img.next{opacity:0}
.tr-pic-box img.in{opacity:1}
.tr-pic.held .tr-pic-box img{filter:saturate(.7) brightness(.93)}
.tr-pic-cap{display:block;margin-top:9px;font-size:.82rem;line-height:1.35;color:#3d6474;font-weight:600}
.tr-pic.held .tr-pic-cap{color:#6f8c99;font-weight:500}
@keyframes tr-kb{from{transform:scale(1)}to{transform:scale(1.06)}}
.tr-pic.compact{flex:0 0 60px;width:60px}
.tr-pic.compact .tr-pic-box{border-radius:7px;box-shadow:none}
.tr-pic-badge{position:absolute;left:0;right:0;bottom:0;font-size:.62rem;font-weight:700;letter-spacing:.03em;
  color:#fff;background:rgba(16,45,56,.62);text-align:center;padding:1px 2px;white-space:nowrap}
.tr-pic.held .tr-pic-badge{background:rgba(16,45,56,.42)}
.tr-nowrow{display:flex;gap:12px;align-items:flex-start}
.tr-nowmain{flex:1;min-width:0}
.tr-docpic{display:none;flex:0 0 60px}
.tr-lb{z-index:6}
.tr-lb-fig{margin:0;background:#fff;border-radius:12px;padding:12px;max-width:min(92vw,720px);
  box-shadow:0 20px 50px -20px rgba(16,45,56,.5)}
.tr-lb-fig img{display:block;width:100%;max-height:calc(100vh - 170px);object-fit:contain;border-radius:8px;background:#eef4f7}
.tr-lb-fig figcaption{display:flex;gap:12px;align-items:center;justify-content:space-between;flex-wrap:wrap;
  margin-top:10px;font-size:.9rem;font-weight:600;color:#102d38}
.tr-lb-actions{display:flex;gap:8px}
.tr-lb-actions .tr-play{flex:0 0 auto}
@media (max-width:1079px){
  .tr-side{display:none}
  .tr-docpic{display:block}
}
@media (prefers-reduced-motion:reduce){
  .tr-pic-box img{transition:none;animation:none}
}
`;
